import {
    ApiAccessRecord,
    ApiServiceDictionaryItem,
    ApiTokenAccessItem,
} from '/react4xp/parts/admin-api-access/types';
import { DEFAULT_LIMIT_PER_DAY } from './constants';

export const sanitizeNumberInput = (value?: string): string => (value || '').replace(/\D/g, '');

export const sanitizeTaxpayerCode = (value?: string): string =>
    sanitizeNumberInput(value).slice(0, 12);

export const getNumberOrNullFromInput = (value?: string): number | null => {
    const onlyNumbers = sanitizeNumberInput(value);

    return onlyNumbers === '' ? null : Number(onlyNumbers);
};

export const buildApiNamesMap = (dictionary: ApiServiceDictionaryItem[]): Record<string, string> =>
    dictionary.reduce<Record<string, string>>((result, item) => {
        result[item.code] = item.name;
        return result;
    }, {});

export const buildApiAccessRecords = (
    dictionary: ApiServiceDictionaryItem[],
    accessItems: ApiTokenAccessItem[]
): ApiAccessRecord[] => {
    const accessByCode = accessItems.reduce<Record<string, ApiTokenAccessItem>>((result, item) => {
        result[item.apiCode] = item;
        return result;
    }, {});

    const dictionaryCodes = new Set(dictionary.map((item) => item.code));

    const dictionaryRecords = dictionary.map<ApiAccessRecord>((item) => {
        const access = accessByCode[item.code];

        return {
            apiCode: item.code,
            apiName: item.name,
            isAllowed: access ? Boolean(access.isAllowed) : true,
            limitPerDay: access ? access.limitPerDay : null,
            limitPerMinute: access ? access.limitPerMinute ?? null : null,
        };
    });

    const extraRecords = accessItems
        .filter((item) => !dictionaryCodes.has(item.apiCode))
        .map<ApiAccessRecord>((item) => ({
            apiCode: item.apiCode,
            isAllowed: Boolean(item.isAllowed),
            limitPerDay: item.limitPerDay,
            limitPerMinute: item.limitPerMinute ?? null,
        }));

    return [...dictionaryRecords, ...extraRecords];
};

export const toApiTokenAccessItems = (records: ApiAccessRecord[]): ApiTokenAccessItem[] =>
    records.map((record) => ({
        apiCode: record.apiCode,
        isAllowed: record.isAllowed,
        limitPerDay: record.isAllowed
            ? record.limitPerDay === null
                ? null
                : Number(record.limitPerDay)
            : null,
        limitPerMinute:
            record.limitPerMinute === null
                ? null
                : Number(record.limitPerMinute),
    }));

export const serializeApiTokenAccessItems = (items: ApiTokenAccessItem[]): string =>
    JSON.stringify(
        [...items]
            .map((item) => ({
                apiCode: item.apiCode,
                isAllowed: Boolean(item.isAllowed),
                limitPerDay: item.limitPerDay === null ? null : Number(item.limitPerDay),
                limitPerMinute:
                    item.limitPerMinute === null
                        ? null
                        : Number(item.limitPerMinute),
            }))
            .sort((first, second) => first.apiCode.localeCompare(second.apiCode))
    );

export const getRequestErrorKey = (
    error: any,
    fallbackKey = 'admin-api-access-error-generic'
): string => {
    if (!error?.response) {
        return 'admin-api-access-error-network';
    }

    if (error.response.status === 400) {
        return 'admin-api-access-error-bad-request';
    }

    if (error.response.status === 403) {
        return 'admin-api-access-error-forbidden';
    }

    if (error.response.status === 500) {
        return 'admin-api-access-error-server';
    }

    return fallbackKey;
};